import { defineStore } from "pinia";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "@/js/firebase";
import { logger } from "@/services/loggerService";

export const useAdminAssetsStore = defineStore("adminAssets", {
  state: () => ({
    // Cache assets by company ID
    assetsByCompany: new Map(), // companyId -> { assets: [], lastLoaded: timestamp, loading: false }
    loading: false,
    error: null,
    initialized: false,
  }),

  getters: {
    /**
     * Get assets for a specific company
     */
    getAssetsForCompany: (state) => (companyId) => {
      const companyData = state.assetsByCompany.get(companyId);
      return companyData?.assets || [];
    },

    /**
     * Check if assets are loaded for a company
     */
    isCompanyLoaded: (state) => (companyId) => {
      const companyData = state.assetsByCompany.get(companyId);
      return companyData && companyData.assets.length > 0;
    },

    /**
     * Check if assets are currently loading for a company
     */
    isCompanyLoading: (state) => (companyId) => {
      const companyData = state.assetsByCompany.get(companyId);
      return companyData?.loading || false;
    },

    /**
     * Get cache stats for debugging
     */
    getCacheStats: (state) => () => {
      const stats = {
        companiesLoaded: state.assetsByCompany.size,
        totalAssets: 0,
        companies: [],
      };

      state.assetsByCompany.forEach((data, companyId) => {
        stats.totalAssets += data.assets.length;
        stats.companies.push({
          companyId,
          assetCount: data.assets.length,
          lastLoaded: data.lastLoaded,
          cacheAge: Date.now() - data.lastLoaded,
        });
      });

      return stats;
    },
  },

  actions: {
    /**
     * Initialize the store
     */
    async init() {
      if (this.initialized) return;
      
      logger.info("[AdminAssetsStore] Initializing admin assets store");
      this.initialized = true;
    },

    /**
     * Load assets for a specific company with caching
     */
    async loadAssetsForCompany(companyId, forceReload = false) {
      if (!companyId) {
        logger.warn("[AdminAssetsStore] No company ID provided");
        return [];
      }

      // Check cache first
      const existingData = this.assetsByCompany.get(companyId);
      const cacheMaxAge = 5 * 60 * 1000; // 5 minutes
      const isCacheValid = existingData && 
        existingData.assets.length > 0 && 
        (Date.now() - existingData.lastLoaded) < cacheMaxAge;

      if (!forceReload && isCacheValid) {
        logger.debug("[AdminAssetsStore] Using cached assets for company", {
          companyId,
          assetCount: existingData.assets.length,
          cacheAge: Date.now() - existingData.lastLoaded,
        });
        return existingData.assets;
      }

      // Check if already loading
      if (existingData?.loading) {
        logger.debug("[AdminAssetsStore] Assets already loading for company", { companyId });
        // Wait for existing load to complete
        return new Promise((resolve) => {
          const checkLoading = () => {
            const data = this.assetsByCompany.get(companyId);
            if (!data?.loading) {
              resolve(data?.assets || []);
            } else {
              setTimeout(checkLoading, 100);
            }
          };
          setTimeout(checkLoading, 100);
        });
      }

      try {
        // Set loading state
        this.assetsByCompany.set(companyId, {
          assets: existingData?.assets || [],
          lastLoaded: existingData?.lastLoaded || 0,
          loading: true,
        });

        logger.info("[AdminAssetsStore] Loading assets for company", { companyId });

        // Query assets for the specific company
        const assetsRef = collection(db, "assets");
        const assetsQuery = query(
          assetsRef,
          where("company", "==", companyId),
          orderBy("uploadDate", "desc")
        );

        const snapshot = await getDocs(assetsQuery);
        const assets = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          assets.push({
            id: doc.id,
            ...data,
          });
        });

        // Update cache
        this.assetsByCompany.set(companyId, {
          assets,
          lastLoaded: Date.now(),
          loading: false,
        });

        logger.info("[AdminAssetsStore] Assets loaded successfully", {
          companyId,
          assetCount: assets.length,
        });

        return assets;
      } catch (error) {
        logger.error("[AdminAssetsStore] Failed to load assets for company", {
          companyId,
          error: error.message,
        });

        // Clear loading state
        const currentData = this.assetsByCompany.get(companyId);
        if (currentData) {
          this.assetsByCompany.set(companyId, {
            ...currentData,
            loading: false,
          });
        }

        this.error = error.message;
        throw error;
      }
    },

    /**
     * Get thumbnail URL for an asset (using the same logic as clientAssetsStore)
     */
    async getThumbnailUrl(asset) {
      if (!asset) return null;

      // If thumbnailUrl is already available, return it
      if (asset.thumbnailUrl) return asset.thumbnailUrl;

      // Nothing else to try. The legacy asset.thumbnail → Firebase getDownloadURL
      // branch went with the migration; Bunny thumbnails arrive on the asset as
      // a decorated `thumbnailUrl` (see clientAssetsStore.getThumbnailUrl).
      return null;
    },

    /**
     * Clear cache for a specific company
     */
    clearCompanyCache(companyId) {
      if (this.assetsByCompany.has(companyId)) {
        this.assetsByCompany.delete(companyId);
        logger.info("[AdminAssetsStore] Cleared cache for company", { companyId });
      }
    },

    /**
     * Clear all cached data
     */
    clearAllCache() {
      this.assetsByCompany.clear();
      logger.info("[AdminAssetsStore] Cleared all asset cache");
    },

    /**
     * Preload assets for multiple companies (for bulk operations)
     */
    async preloadCompanies(companyIds) {
      logger.info("[AdminAssetsStore] Preloading assets for companies", {
        companyCount: companyIds.length,
      });

      const loadPromises = companyIds.map((companyId) =>
        this.loadAssetsForCompany(companyId).catch((error) => {
          logger.warn("[AdminAssetsStore] Failed to preload company assets", {
            companyId,
            error: error.message,
          });
          return [];
        })
      );

      await Promise.allSettled(loadPromises);
      logger.info("[AdminAssetsStore] Preload completed");
    },

    /**
     * Reset store state
     */
    reset() {
      this.assetsByCompany.clear();
      this.loading = false;
      this.error = null;
      this.initialized = false;
      logger.info("[AdminAssetsStore] Store reset");
    },
  },
}); 